import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Card, Badge, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import firebaseApp from "../../../firebase/credenciales";
import * as FaIcons from "react-icons/fa";

const HiroChat = ({ ruc }) => {
  const [messages, setMessages] = useState([
    { 
      sender: 'hiro', 
      text: '¡Hola! Soy Hiro, tu especialista en búsqueda de candidatos. Describe el perfil que buscas y te ayudaré a encontrar los 5 mejores candidatos de nuestra base de datos.',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  ]);
  const [profileQuery, setProfileQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll al último mensaje
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Función para buscar candidatos
  const searchCandidates = async () => {
    if (!profileQuery.trim()) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    
    // Agregar mensaje del usuario
    const userMessage = { sender: 'user', text: profileQuery, timestamp };
    setMessages(prev => [...prev, userMessage]);
    setProfileQuery('');
    setLoading(true);

    try {
      // Llamar a la función correcta: hiro_semanticSearch (v2 URL)
      const response = await axios.post("https://hiro-semanticsearch-pdgfj3iftq-uc.a.run.app", {
        query: userMessage.text,
        ruc,
        limit: 5
      });

      const results = response.data.results || [];
      
      let responseText = '';
      let candidateCards = [];

      if (results.length > 0) {
        responseText = `🎯 Encontré ${results.length} candidato(s) que coinciden con tu búsqueda:`;
        // Adaptamos el formato de resultados al que espera CandidateCard
        candidateCards = results.map(r => ({
          name: r.candidateName || 'Sin nombre',
          matchedRole: r.matchedRole || r.profesion || 'No especificado',
          experience: r.aniosExperiencia ? `${r.aniosExperiencia} año(s)` : 'No especificado',
          // similarity = 1 - cosineDistance. Re-escalamos: distancia 0.3→70%, distancia 0.8→20%
          relevanceScore: r.similarity,  // valor raw del backend para mostrar diferenciado
          justificacion: r.justificacion || '',
          storagePath: r.storagePath || '',
          summary: null,
          skills: [],
          documentName: r.documentId || '',
          seniorityLevel: null
        }));
      } else {
        responseText = '😔 No encontré candidatos que coincidan exactamente con el perfil solicitado. Te sugiero ajustar los criterios de búsqueda o verificar que hayas subido CVs para este RUC.';
      }

      const hiroMessage = { 
        sender: 'hiro', 
        text: responseText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        candidates: candidateCards,
        searchStats: { totalSearched: results.length },
        type: 'candidate_search'
      };
      
      setMessages(prev => [...prev, hiroMessage]);
    } catch (error) {
      console.error('Error en búsqueda Hiro:', error);
      const errorMessage = { 
        sender: 'hiro', 
        text: `Lo siento, ocurrió un error al buscar candidatos: ${error.response?.data?.message || error.message}. Por favor, intenta nuevamente.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // Manejar Enter para enviar
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      searchCandidates();
    }
  };

  // Componente para mostrar tarjeta de candidato
  const CandidateCard = ({ candidate, index }) => {
    const [opening, setOpening] = useState(false);

    const handleViewCV = async () => {
      if (!candidate.storagePath) {
        alert("No se encontró la ruta del documento.");
        return;
      }
      
      setOpening(true);
      try {
        const storage = getStorage(firebaseApp);
        const fileRef = ref(storage, candidate.storagePath);
        const url = await getDownloadURL(fileRef);
        window.open(url, '_blank');
      } catch (error) {
        console.error("Error abriendo CV:", error);
        alert("No se pudo obtener el enlace del CV.");
      } finally {
        setOpening(false);
      }
    };

    return (
      <Card className="mb-3" style={{ border: '1px solid #e3f2fd', backgroundColor: '#f8f9ff', borderRadius: '12px' }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h6 className="mb-0 text-primary fw-bold">
              #{index + 1} {candidate.name}
            </h6>
            <Badge 
              bg={candidate.relevanceScore > 0.4 ? 'success' : candidate.relevanceScore > 0.15 ? 'warning' : 'secondary'} 
              style={{ fontSize: '0.75em' }}
            >
              {candidate.relevanceScore > 0.6 ? '⭐ Alta' : candidate.relevanceScore > 0.3 ? '✅ Media' : '🔵 Encontrado'}
            </Badge>
          </div>
          
          <div className="mb-2">
            <p className="mb-1 text-dark" style={{ fontSize: '1em', fontWeight: '500' }}>
              🎯 <span style={{ color: '#8B4513' }}>{candidate.matchedRole}</span>
            </p>
            <p className="mb-1 text-muted" style={{ fontSize: '0.9em' }}>
              <strong>Exp. Total:</strong> {candidate.experience}
            </p>
          </div>

          {candidate.justificacion && (
            <div className="mb-3 p-3 rounded" style={{ 
              backgroundColor: '#fffdf9', 
              borderLeft: '5px solid #8B4513',
              boxShadow: '0 2px 4px rgba(139, 69, 19, 0.1)',
              margin: '10px 0'
            }}>
              <p className="mb-0 text-dark" style={{ fontSize: '0.95em', lineHeight: '1.4' }}>
                <FaIcons.FaMagic className="me-2" style={{ color: '#8B4513' }} />
                <strong>Match de IA:</strong> <em>"{candidate.justificacion}"</em>
              </p>
            </div>
          )}
          
          <div className="mt-2 d-flex justify-content-between align-items-center">
            <small className="text-muted" style={{ fontSize: '0.8em' }}>
              📄 ID: {candidate.documentName}
            </small>
            
            <Button 
              size="sm" 
              variant="outline-primary"
              onClick={handleViewCV}
              disabled={opening || !candidate.storagePath}
              title={!candidate.storagePath ? "Documento fuente no disponible" : "Abrir CV"}
              style={{ 
                fontSize: '0.8em', 
                borderRadius: '20px',
                borderColor: candidate.storagePath ? '#8B4513' : '#d1d5db',
                color: candidate.storagePath ? '#8B4513' : '#9ca3af'
              }}
            >
              {opening ? <Spinner animation="border" size="sm" className="me-1" /> : <FaIcons.FaExternalLinkAlt className="me-1" />}
              {opening ? 'Abriendo...' : !candidate.storagePath ? 'CV no disponible' : 'Ver CV'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const styles = {
    chatContainer: {
      maxWidth: '900px',
      margin: '20px auto',
      height: '700px',
      background: '#f0f2f5',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    chatHeader: {
      background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
      color: 'white',
      padding: '16px 20px',
      borderRadius: '16px 16px 0 0',
      borderBottom: '3px solid #D2691E'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    avatar: {
      width: '48px',
      height: '48px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      backdropFilter: 'blur(10px)',
      border: '2px solid rgba(255, 255, 255, 0.3)'
    },
    avatarText: {
      fontSize: '20px',
      fontWeight: '600',
      color: 'white'
    },
    statusIndicator: {
      position: 'absolute',
      bottom: '2px',
      right: '2px',
      width: '12px',
      height: '12px',
      background: '#4ade80',
      borderRadius: '50%',
      border: '2px solid white'
    },
    chatInfo: {
      flex: 1
    },
    chatTitle: {
      margin: 0,
      fontSize: '18px',
      fontWeight: '600',
      color: 'white'
    },
    chatSubtitle: {
      margin: 0,
      fontSize: '13px',
      color: 'rgba(255, 255, 255, 0.8)',
      fontWeight: '400'
    },
    chatMessages: {
      flex: 1,
      overflow: 'hidden',
      background: '#f0f2f5'
    },
    messagesContainer: {
      height: '100%',
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    messageWrapper: {
      display: 'flex',
      marginBottom: '4px'
    },
    messageWrapperUser: {
      justifyContent: 'flex-end'
    },
    messageWrapperHiro: {
      justifyContent: 'flex-start'
    },
    messageBubble: {
      maxWidth: '85%',
      padding: '12px 16px',
      borderRadius: '18px',
      position: 'relative',
      wordWrap: 'break-word',
      animation: 'messageSlideIn 0.3s ease-out'
    },
    messageBubbleUser: {
      background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
      color: 'white',
      borderBottomRightRadius: '4px',
      marginLeft: '48px',
      boxShadow: '0 4px 12px rgba(139, 69, 19, 0.2)'
    },
    messageBubbleHiro: {
      background: 'white',
      color: '#1f2937',
      borderBottomLeftRadius: '4px',
      marginRight: '48px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
    },
    messageBubbleError: {
      background: '#fee2e2',
      border: '1px solid #fecaca',
      color: '#dc2626'
    },
    messageContent: {
      fontSize: '14px',
      lineHeight: '1.4',
      marginBottom: '4px'
    },
    messageMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      marginTop: '4px'
    },
    messageTime: {
      fontSize: '11px',
      opacity: 0.7,
      fontWeight: '400'
    },
    searchStats: {
      fontSize: '10px',
      opacity: 0.6,
      fontStyle: 'italic'
    },
    candidatesContainer: {
      marginTop: '8px',
      maxHeight: '400px',
      overflowY: 'auto'
    },
    typingIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '8px 0'
    },
    typingDots: {
      display: 'flex',
      gap: '4px'
    },
    typingDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#9ca3af',
      animation: 'typingAnimation 1.4s infinite ease-in-out'
    },
    chatInputArea: {
      background: 'white',
      padding: '16px 20px',
      borderTop: '1px solid #e5e7eb'
    },
    inputGroup: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '12px',
      width: '100%'
    },
    inputWrapper: {
      flex: 1,
      position: 'relative'
    },
    messageInput: {
      border: '2px solid #e5e7eb',
      borderRadius: '24px',
      padding: '12px 20px',
      fontSize: '14px',
      resize: 'none',
      minHeight: '44px',
      maxHeight: '120px',
      transition: 'all 0.2s ease',
      background: '#f9fafb',
      width: '100%',
      outline: 'none'
    },
    sendButton: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      flexShrink: 0,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(139, 69, 19, 0.3)'
    },
    sendButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  };

  return (
    <div style={styles.chatContainer}>
      {/* Header del Chat */}
      <div style={styles.chatHeader}>
        <div style={styles.headerContent}>
          <div style={styles.avatar}>
            <span style={styles.avatarText}>H</span>
            <div style={styles.statusIndicator}></div>
          </div>
          <div style={styles.chatInfo}>
            <h4 style={styles.chatTitle}>Hiro Assistant</h4>
            <p style={styles.chatSubtitle}>Especialista en Búsqueda de Candidatos • En línea</p>
          </div>
        </div>
      </div>

      {/* Área de Mensajes */}
      <div style={styles.chatMessages}>
        <div style={styles.messagesContainer}>
          {messages.map((msg, index) => (
            <div key={index} style={{
              ...styles.messageWrapper,
              ...(msg.sender === 'user' ? styles.messageWrapperUser : styles.messageWrapperHiro)
            }}>
              <div style={{
                ...styles.messageBubble,
                ...(msg.sender === 'user' ? styles.messageBubbleUser : styles.messageBubbleHiro),
                ...(msg.isError ? styles.messageBubbleError : {})
              }}>
                <div style={styles.messageContent}>
                  {msg.text}
                </div>
                
                {/* Mostrar candidatos si los hay */}
                {msg.candidates && msg.candidates.length > 0 && (
                  <div style={styles.candidatesContainer}>
                    {msg.candidates.map((candidate, idx) => (
                      <CandidateCard key={idx} candidate={candidate} index={idx} />
                    ))}
                  </div>
                )}
                
                <div style={styles.messageMeta}>
                  <span style={styles.messageTime}>{msg.timestamp}</span>
                  {msg.searchStats && (
                    <span style={styles.searchStats}>
                      🔍 {msg.searchStats.totalSearched || 0} CVs analizados
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {/* Indicador de búsqueda */}
          {loading && (
            <div style={{...styles.messageWrapper, ...styles.messageWrapperHiro}}>
              <div style={{...styles.messageBubble, ...styles.messageBubbleHiro}}>
                <div style={styles.typingIndicator}>
                  <div style={styles.typingDots}>
                    <span style={{...styles.typingDot, animationDelay: '0s'}}></span>
                    <span style={{...styles.typingDot, animationDelay: '0.2s'}}></span>
                    <span style={{...styles.typingDot, animationDelay: '0.4s'}}></span>
                  </div>
                </div>
                <div style={styles.messageMeta}>
                  <span style={styles.messageTime}>Buscando candidatos...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div style={styles.chatInputArea}>
        <Form onSubmit={(e) => { e.preventDefault(); searchCandidates(); }}>
          <div style={styles.inputGroup}>
            <div style={styles.inputWrapper}>
              <textarea
                ref={inputRef}
                style={styles.messageInput}
                placeholder="Describe el perfil que buscas: 'Desarrollador React con 3+ años', 'Marketing Manager bilingüe', 'Contador con experiencia en NIIF'..."
                value={profileQuery}
                onChange={(e) => setProfileQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                rows={1}
              />
            </div>
            <Button 
              type="submit" 
              style={{
                ...styles.sendButton,
                ...(loading || !profileQuery.trim() ? styles.sendButtonDisabled : {})
              }}
              disabled={loading || !profileQuery.trim()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 21L16.514 16.506" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 10C19 15.5 15.5 19 10 19C4.5 19 1 15.5 1 10C1 4.5 4.5 1 10 1C15.5 1 19 4.5 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Button>
          </div>
        </Form>
      </div>

      {/* Estilos CSS en línea para animaciones */}
      <style jsx>{`
        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes typingAnimation {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }

        .candidates-container::-webkit-scrollbar {
          width: 4px;
        }

        .candidates-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .candidates-container::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }

        .send-button:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
        }

        @media (max-width: 768px) {
          .chat-container {
            height: 100vh;
            border-radius: 0;
            max-width: 100%;
          }
          
          .message-bubble {
            max-width: 90%;
          }
        }
      `}</style>
    </div>
  );
};

export default HiroChat;